import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { ServiceDetails } from '@/types/organization.types';

interface ServiceManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: ServiceDetails | null;
  onSave: (service: Partial<ServiceDetails>) => Promise<void>;
}

const serviceTypes = [
  'SEO',
  'SEM',
  'Social Media',
  'Content Marketing',
  'Email Marketing',
  'Web Development',
  'Web Design',
  'Branding',
  'PR',
  'Analytics',
  'Consulting',
  'Other',
];

const serviceStatuses = [
  { value: 'active', label: 'Active', color: 'text-green-600' },
  { value: 'pending', label: 'Pending', color: 'text-yellow-600' },
  { value: 'paused', label: 'Paused', color: 'text-orange-600' },
  { value: 'completed', label: 'Completed', color: 'text-blue-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-600' },
];

const billingFrequencies = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'one-time', label: 'One-Time' },
  { value: 'custom', label: 'Custom' },
];

export function ServiceManagementDialog({
  open,
  onOpenChange,
  service,
  onSave,
}: ServiceManagementDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<ServiceDetails>>({
    name: service?.name || '',
    type: service?.type || '',
    status: service?.status || 'active',
    start_date: service?.start_date || new Date().toISOString(),
    end_date: service?.end_date || null,
    monthly_value: service?.monthly_value || 0,
    billing_frequency: service?.billing_frequency || 'monthly',
    description: service?.description || '',
    deliverables: service?.deliverables || [],
    kpis: service?.kpis || [],
    notes: service?.notes || '',
  });

  const [newDeliverable, setNewDeliverable] = useState('');
  const [newKpi, setNewKpi] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.type) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      status: 'active',
      start_date: new Date().toISOString(),
      end_date: null,
      monthly_value: 0,
      billing_frequency: 'monthly',
      description: '',
      deliverables: [],
      kpis: [],
      notes: '',
    });
    setNewDeliverable('');
    setNewKpi('');
  };

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData((prev) => ({
        ...prev,
        deliverables: [...(prev.deliverables || []), newDeliverable.trim()],
      }));
      setNewDeliverable('');
    }
  };

  const removeDeliverable = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      deliverables: prev.deliverables?.filter((_, i) => i !== index) || [],
    }));
  };

  const addKpi = () => {
    if (newKpi.trim()) {
      setFormData((prev) => ({
        ...prev,
        kpis: [...(prev.kpis || []), newKpi.trim()],
      }));
      setNewKpi('');
    }
  };

  const removeKpi = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      kpis: prev.kpis?.filter((_, i) => i !== index) || [],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{service ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            <DialogDescription>
              {service
                ? 'Update the service details below'
                : 'Add a new service to track for this organization'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Service Name and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., SEO Campaign Q1 2024"
                  required
                />
              </div>
              <div>
                <Label htmlFor="type">Service Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status and Billing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        <span className={status.color}>{status.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="billing">Billing Frequency</Label>
                <Select
                  value={formData.billing_frequency}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, billing_frequency: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {billingFrequencies.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.start_date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.start_date
                        ? format(new Date(formData.start_date), 'PPP')
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.start_date ? new Date(formData.start_date) : undefined}
                      onSelect={(date) =>
                        setFormData((prev) => ({
                          ...prev,
                          start_date: date?.toISOString() || null,
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.end_date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.end_date
                        ? format(new Date(formData.end_date), 'PPP')
                        : 'No end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.end_date ? new Date(formData.end_date) : undefined}
                      onSelect={(date) =>
                        setFormData((prev) => ({
                          ...prev,
                          end_date: date?.toISOString() || null,
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Monthly Value */}
            <div>
              <Label htmlFor="value">Monthly Value ($)</Label>
              <Input
                id="value"
                type="number"
                min="0"
                step="0.01"
                value={formData.monthly_value}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    monthly_value: parseFloat(e.target.value) || 0,
                  }))
                }
                placeholder="0.00"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the service scope and objectives..."
                rows={3}
              />
            </div>

            {/* Deliverables */}
            <div>
              <Label>Deliverables</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newDeliverable}
                    onChange={(e) => setNewDeliverable(e.target.value)}
                    placeholder="Add a deliverable"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
                  />
                  <Button type="button" variant="outline" onClick={addDeliverable}>
                    Add
                  </Button>
                </div>
                {formData.deliverables && formData.deliverables.length > 0 && (
                  <div className="space-y-1">
                    {formData.deliverables.map((deliverable, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <span className="text-sm">{deliverable}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDeliverable(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* KPIs */}
            <div>
              <Label>Key Performance Indicators (KPIs)</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newKpi}
                    onChange={(e) => setNewKpi(e.target.value)}
                    placeholder="Add a KPI"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKpi())}
                  />
                  <Button type="button" variant="outline" onClick={addKpi}>
                    Add
                  </Button>
                </div>
                {formData.kpis && formData.kpis.length > 0 && (
                  <div className="space-y-1">
                    {formData.kpis.map((kpi, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <span className="text-sm">{kpi}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeKpi(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about this service..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {service ? 'Update Service' : 'Add Service'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
